import { useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useChat, type Message } from '@/contexts/ChatContext'
import { useUserRole } from '@/contexts/UserRoleContext'
import { useAuth } from '@/contexts/AuthContext'
import { usePinnedCardsContext } from '@/contexts/PinnedCardsContext'
import { MessageBubble } from './MessageBubble'
import { TypingIndicator } from './TypingIndicator'

export function MessageList() {
  const { messages, isTyping, isStreaming, quickReplies } = useChat()
  const { currentUser } = useUserRole()
  const { currentEventId } = useAuth()
  const { pinCard } = usePinnedCardsContext()

  const handlePin = useCallback(
    (botMsg: Message, index: number) => {
      let question = 'Chat question'
      for (let i = index - 1; i >= 0; i--) {
        if (messages[i].type === 'user') {
          question = messages[i].content
          break
        }
      }
      pinCard(question, botMsg.content, botMsg.chart, currentEventId)
    },
    [messages, pinCard, currentEventId]
  )
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Pull the first word of the full name so greetings read SMS-friendly.
  // Falls back to "there" when no profile is loaded yet.
  const firstName = currentUser?.name?.trim().split(/\s+/)[0] ?? 'there'

  // Auto-scroll to bottom when new messages arrive, streaming updates, OR
  // the quick-replies panel appears/changes. Without the quickReplies
  // dependency, the panel's height:auto animation shrinks the scroll area
  // without re-anchoring — the last bot message gets clipped behind the
  // quick-replies strip (Brian's V2 chat-fault E: "shouldn't cut off or
  // cover up the reply"). We also fire a delayed follow-up scroll ~320ms
  // after the panel's framer-motion 200ms height animation completes, so
  // the final anchor lands on the real post-animation viewport.
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }
  }, [messages, isTyping, isStreaming, quickReplies.length])

  useEffect(() => {
    if (quickReplies.length === 0) return
    const t = window.setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, 320)
    return () => window.clearTimeout(t)
  }, [quickReplies.length])

  // Show typing indicator only when waiting for first chunk (not during streaming)
  const showTyping = isTyping && !isStreaming

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-6 space-y-4 scroll-smooth"
      role="log"
      aria-live="polite"
      aria-label="Chat messages"
    >
      <AnimatePresence mode="popLayout">
        {messages.map((message, index) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
            }}
          >
            <MessageBubble
              message={message}
              onPin={message.type === 'bot' ? () => handlePin(message, index) : undefined}
            />
          </motion.div>
        ))}

        {/* Typing Indicator — only when waiting for first chunk */}
        {showTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <TypingIndicator />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state — single SMS-friendly welcome per Brian's product vision
          ("sparse, friendly, example-led" — no product jargon). Starter-prompt
          pills render below in ChatInterface, so this panel carries only the
          greeting + a one-line framing of what the agent can do. */}
      {messages.length === 0 && !isTyping && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="flex flex-col items-center justify-center text-center py-10 px-4"
          data-testid="chat-empty-state"
        >
          <div
            className="h-14 w-14 rounded-full bg-butler-sparkle flex items-center justify-center shadow-card-soft mb-3"
            aria-hidden="true"
          >
            <span className="text-2xl">👋</span>
          </div>
          <h2 className="font-display text-lg font-semibold text-butler-800 dark:text-butler-100">
            Hi {firstName}
          </h2>
          <p className="mt-1 max-w-xs text-sm text-ink-soft dark:text-slate-400">
            What would you like to dig into today? Try one of the suggestions
            below, or just start typing.
          </p>
        </motion.div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} className="h-1" />
    </div>
  )
}
